import {
  MarketingPageElementLocation,
  MarketingPageElementType,
  sendBeaconEvent
} from '@lucid/gatsby-theme-lucid';
import { graphql } from 'gatsby';
import { Box, Heading } from 'lucid-components-react';
import React, { useEffect, useRef, useState } from 'react';
import LazyLoad from 'react-lazyload';

import LucidContainer from 'components/lucid-container/lucid-container';
import NineteenCta, {
  nineteenCtaThemes
} from 'components/nineteen/nineteen-cta/nineteen-cta';
import {
  Drupal_FieldParagraphNineteenArticleFieldNineteenCtas,
  Drupal_FieldParagraphNineteenInsightsFieldNineteenArticles,
  Drupal_ParagraphNineteenArticle,
  Drupal_ParagraphNineteenInsights
} from 'generated/graphql';
import { Text } from 'theme/typography';
import { LucidTheme } from 'types/types';
import { getProcessedValue } from 'utils/helpers';

const listContentCss = (theme: LucidTheme) => ({
  margin: theme.space[0],
  maxWidth: '333px',
  color: 'inherit',
  [`@media screen and (max-width: ${theme.breakpoints.tablet})`]: {
    maxWidth: 'unset'
  },
  [`@media screen and (max-width: ${theme.breakpoints.mobile})`]: {
    paddingRight: theme.space[4]
  }
});

interface Props {
  entity: Drupal_ParagraphNineteenInsights;
}

const NineteenInsights = ({
  entity: { fieldHeader, fieldLongSubheader, fieldNineteenArticles }
}: Props) => {
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const isInsightClicked = useRef(false);
  const thisInsight = useRef(null);
  const chosenArticle = (fieldNineteenArticles as Drupal_FieldParagraphNineteenInsightsFieldNineteenArticles[])[
    activeSectionIndex
  ];

  const chosenArticleEntity = chosenArticle
    ? (chosenArticle.entity as Drupal_ParagraphNineteenArticle)
    : null;

  useEffect(() => {
    if (isInsightClicked.current && window.innerWidth <= 500) {
      const current = (thisInsight.current as unknown) as HTMLElement;

      const activeInsight = current.querySelector(
        '.nineteen-insights__list-item--active'
      );

      if (activeInsight) {
        activeInsight.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [activeSectionIndex]);

  const getInsightContent = (
    articleEntity: Drupal_ParagraphNineteenArticle,
    isMobile: boolean,
    isActive: boolean
  ) => {
    const image = (
      <img alt={articleEntity.fieldAltText!} src={articleEntity.fieldImage!} />
    );

    return (
      <Box
        css={(theme: LucidTheme) => [
          {
            flex: '0 1 55%',
            maxWidth: '55%',
            [`@media screen and (max-width: ${theme.breakpoints.computer})`]: {
              flex: '0 1 48%',
              maxWidth: '48%'
            },
            [`@media screen and (max-width: ${theme.breakpoints.tablet})`]: {
              flex: '0 1 100%',
              maxWidth: '100%'
            },
            [`@media screen and (max-width: ${theme.breakpoints.mobile})`]: {
              display: isMobile && isActive ? 'inline-flex' : 'none'
            }
          },
          isMobile &&
          isActive && {
            display: 'inline-flex',
            marginTop: theme.space[3]
          },
          isMobile && {
            display: 'none'
          }
        ]}
      >
        <Box
          css={(theme: LucidTheme) => ({
            marginBottom: theme.space[5],
            maxWidth: '100%',
            maxHeight: '100%',
            height: 'auto',
            width: 'auto',
            display: 'block',
            [`@media screen and (max-width: ${theme.breakpoints.mobile})`]: {
              order: 1,
              margin: theme.space[0]
            }
          })}
        >
          <LazyLoad>{image}</LazyLoad>
        </Box>
        <Box
          css={(theme: LucidTheme) => ({
            margin: `${theme.space[0]} ${theme.space[0]} ${theme.space[5]}px`,
            [`@media screen and (max-width: ${theme.breakpoints.tablet})`]: {
              margin: `${theme.space[0]} ${theme.space[0]} ${theme.space[3]}px`
            },
            [`@media screen and (max-width: ${theme.breakpoints.mobile})`]: {
              order: 0
            }
          })}
        >
          <Box
            dangerouslySetInnerHTML={{
              __html: getProcessedValue(articleEntity.fieldBody)
            }}
          />
          {(articleEntity.fieldNineteenCtas as Drupal_FieldParagraphNineteenArticleFieldNineteenCtas[]).map(
            ({ entity }) => (
              <NineteenCta
                key={entity!.entityId}
                entity={entity!}
                theme={nineteenCtaThemes.arrow}
              />
            )
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      ref={thisInsight}
      as="section"
      css={(theme: LucidTheme) => ({
        paddingTop: theme.space[6],
        paddingBottom: theme.space[6],
        [`@media screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
          paddingTop: theme.space[7],
          paddingBottom: theme.space[0]
        }
      })}
    >
      <LucidContainer>
        {fieldHeader && (
          <Heading as="h2" variant="subheadS">
            {fieldHeader}
          </Heading>
        )}
        {fieldLongSubheader && (
          <Text
            css={(theme: LucidTheme) => ({
              maxWidth: '36rem',
              margin: `${theme.space[0]} ${theme.space[0]} ${theme.space[8]}px`,
              [`@media screen and (max-width: ${theme.breakpoints.tablet})`]: {
                margin: `${theme.space[0]} ${theme.space[0]} ${theme.space[7]}px`
              }
            })}
            textStyles="body"
          >
            {fieldLongSubheader}
          </Text>
        )}
        <Box
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap'
          }}
        >
          <ul
            css={(theme: LucidTheme) => ({
              listStyle: 'none',
              display: 'inline-block',
              margin: theme.space[0],
              flex: '0 1 40%',
              [`@media screen and (max-width: ${theme.breakpoints.tablet})`]: {
                flex: '0 1 100%',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                margin: `${theme.space[0]} ${theme.space[0]} ${theme.space[7]}px`
              }
            })}
          >
            {(fieldNineteenArticles as Drupal_FieldParagraphNineteenInsightsFieldNineteenArticles[]).map(
              (article, i) => {
                const entity = article.entity as Drupal_ParagraphNineteenArticle;
                const isActive = i === activeSectionIndex;
                return (
                  <li
                    key={entity.entityId}
                    className={
                      isActive ? 'nineteen-insights__list-item--active' : ''
                    }
                    css={(theme: LucidTheme) => [
                      {
                        borderRadius: theme.borderRadius,
                        background: theme.colors.white,
                        transition:
                          'border-color .25s ease, transform 0.15s ease-in',
                        position: 'relative',
                        marginBottom: theme.space[3],
                        flex: '0 1 90%',
                        transform: 'translateX(0)',
                        [`@media screen and (max-width: ${theme.breakpoints.tablet})`]: {
                          background: 'transparent',
                          flex: '0 1 48%',
                          color: theme.colors.coolGrey[30]
                        },
                        [`@media screen and (max-width: ${theme.breakpoints.mobile})`]: {
                          flex: '0 1 100%',
                          color: theme.colors.coolGrey[90]
                        }
                      },
                      isActive && {
                        transform: 'translateX(5%)',
                        [`@media screen and (max-width: ${theme.breakpoints.tablet})`]: {
                          color: theme.colors.coolGrey[90]
                        },
                        [`@media screen and (max-width: ${theme.breakpoints.mobile})`]: {
                          marginBottom: theme.space[5],
                          transform: 'none'
                        }
                      }
                    ]}
                  >
                    <button
                      className="nineteen-insights__list-button"
                      css={(theme: LucidTheme) => [
                        {
                          boxShadow: theme.shadows.listItem,
                          width: '100%',
                          borderWidth: `1px 1px 1px ${theme.space[3]}px`,
                          borderStyle: 'solid',
                          borderColor: theme.colors.coolGrey[5],
                          color: theme.colors.coolGrey[70],
                          padding: entity.fieldSubheader
                            ? theme.space[2]
                            : `${theme.space[3]}px ${theme.space[2]}px`,
                          margin: theme.space[0],
                          textAlign: 'left',
                          background: 'transparent',
                          position: 'relative',
                          [`@media screen and (max-width: ${theme.breakpoints.mobile})`]: {
                            padding: theme.space[4]
                          },
                          '&:hover': {
                            cursor: 'pointer'
                          },
                          '&:focus': {
                            outline: 'none'
                          }
                        },
                        isActive && {
                          borderColor: theme.colors.orange[45],
                          color: theme.colors.coolGrey[100]
                        }
                      ]}
                      onClick={() => {
                        if (window.screen.width <= 500 && isActive) {
                          isInsightClicked.current = false;
                          setActiveSectionIndex(-1);
                        } else {
                          entity.fieldBeaconElementDetail &&
                            sendBeaconEvent({
                              name: 'clickedMarketingPageElement',
                              elementLocation:
                                MarketingPageElementLocation.body,
                              elementDetail: entity.fieldBeaconElementDetail,
                              elementType: MarketingPageElementType.accordion
                            });
                          isInsightClicked.current = true;
                          setActiveSectionIndex(i);
                        }
                      }}
                      type="button"
                    >
                      <Text css={listContentCss} textStyles="card">
                        <strong>{entity.fieldHeading}</strong>
                      </Text>
                      <Text
                        css={(theme: LucidTheme) => [
                          listContentCss(theme),
                          {
                            [`@media screen and (max-width: ${theme.breakpoints.tablet})`]: {
                              display: 'none'
                            },
                            [`@media screen and (max-width: ${theme.breakpoints.mobile})`]: {
                              display: 'block'
                            }
                          }
                        ]}
                        textStyles="quoteXS"
                      >
                        {entity.fieldSubheader}
                      </Text>
                    </button>
                    {chosenArticleEntity &&
                      getInsightContent(chosenArticleEntity, true, isActive)}
                  </li>
                );
              }
            )}
          </ul>
          {chosenArticleEntity &&
            getInsightContent(chosenArticleEntity, false, false)}
        </Box>
      </LucidContainer>
    </Box>
  );
};

export default NineteenInsights;

export const query = graphql`
  fragment nineteenInsights on Drupal_ParagraphNineteenInsights {
    fieldHeader
    fieldLongSubheader
    fieldNineteenArticles {
      entity {
        ...nineteenArticle
      }
    }
  }
`;
